import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";

export type InvitationAddonMode = "none" | "paid" | "gift" | "promo";

export interface InvitationAddonState {
  mode: InvitationAddonMode;
  amount: number;
  promoCode: string;
  alreadyActive: boolean;
}

interface Props {
  tenantId: string;
  clientId: string;
  contractId?: string; // present when editing
  value: InvitationAddonState;
  onChange: (val: InvitationAddonState) => void;
}

const DEFAULT_AMOUNT = 50;

export default function InvitationAddonSection({
  tenantId,
  clientId,
  contractId,
  value,
  onChange,
}: Props) {
  const [moduleEnabled, setModuleEnabled] = useState<boolean | null>(null);
  const [checking, setChecking]           = useState(false);

  /* ── Check if tenant has module active AND if client already has addon ── */
  useEffect(() => {
    if (!tenantId || !clientId) return;
    const check = async () => {
      setChecking(true);
      try {
        const [moduleRes, addonRes] = await Promise.all([
          supabase
            .from("tenant_modules")
            .select("is_enabled")
            .eq("tenant_id", tenantId)
            .eq("module_id",
              // get module id for digital_invitations
              (await supabase
                .from("saas_modules")
                .select("id")
                .eq("module_key", "digital_invitations")
                .maybeSingle()
              ).data?.id ?? ""
            )
            .maybeSingle(),
          supabase
            .from("client_addons")
            .select("id, status")
            .eq("tenant_id", tenantId)
            .eq("client_id", clientId)
            .eq("addon_key", "digital_invitation")
            .maybeSingle(),
        ]);

        const enabled = moduleRes.data?.is_enabled === true;
        setModuleEnabled(enabled);

        const alreadyActive = addonRes.data?.status === "active";
        if (alreadyActive) {
          onChange({ ...value, alreadyActive: true, mode: "none" });
        } else {
          onChange({ ...value, alreadyActive: false });
        }
      } finally {
        setChecking(false);
      }
    };
    check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, clientId]);

  const set = (patch: Partial<InvitationAddonState>) =>
    onChange({ ...value, ...patch });

  // Module not enabled for this tenant
  if (moduleEnabled === false) return null;

  // Loading
  if (moduleEnabled === null || checking) {
    return (
      <div className="h-6 w-48 bg-slate-100 rounded animate-pulse" />
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-100">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
          <i className="ri-mail-open-line text-[#C8A96E] text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#0f172a] text-sm font-semibold">Invitación Digital</p>
          <p className="text-slate-400 text-xs">Add-on disponible para este contrato</p>
        </div>
        {value.alreadyActive ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
            <i className="ri-checkbox-circle-fill text-xs"></i>
            Ya activa
          </span>
        ) : (
          <span className="text-xs text-slate-400">No activada</span>
        )}
      </div>

      {/* Already active — show info only */}
      {value.alreadyActive ? (
        <div className="px-4 py-3 flex items-center gap-2 text-xs text-slate-500">
          <i className="ri-information-line text-[#C8A96E]"></i>
          Este cliente ya tiene la invitación digital activa. No se generará un cargo adicional.
        </div>
      ) : (
        <div className="px-4 py-4 space-y-4">
          {/* Mode selector */}
          <div>
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2">Tipo de activación</p>
            <div className="grid grid-cols-3 gap-2">
              {([
                { id: "none",  label: "No incluir",   icon: "ri-close-circle-line",  desc: "Sin invitación" },
                { id: "paid",  label: "Cobrado",       icon: "ri-money-dollar-circle-line", desc: `Cargo $${value.amount}` },
                { id: "gift",  label: "Regalo",        icon: "ri-gift-line",          desc: "Sin costo" },
                // promo as 4th pill below
              ] as const).map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => set({ mode: opt.id })}
                  type="button"
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                    value.mode === opt.id
                      ? "border-[#C8A96E] bg-[#C8A96E]/8 text-[#C8A96E]"
                      : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${opt.icon} text-base`}></i>
                  </span>
                  <span>{opt.label}</span>
                  <span className={`text-[10px] ${value.mode === opt.id ? "text-[#C8A96E]/70" : "text-slate-300"}`}>{opt.desc}</span>
                </button>
              ))}
            </div>

            {/* Promo code as separate row */}
            <button
              onClick={() => set({ mode: "promo" })}
              type="button"
              className={`mt-2 w-full flex items-center gap-2 p-2.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                value.mode === "promo"
                  ? "border-[#C8A96E] bg-[#C8A96E]/8 text-[#C8A96E]"
                  : "border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-coupon-3-line text-base"></i>
              </span>
              <span>Código Promocional</span>
              {value.mode === "promo" && (
                <span className="ml-auto text-[10px] text-[#C8A96E]/70">Introducir código abajo</span>
              )}
            </button>
          </div>

          {/* Paid — amount */}
          {value.mode === "paid" && (
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
                Monto del cargo (USD)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={value.amount}
                  onChange={(e) => set({ amount: Number(e.target.value) })}
                  className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
              </div>
              <p className="text-slate-400 text-xs mt-1.5 flex items-center gap-1">
                <i className="ri-information-line"></i>
                Se creará un cargo en <strong>Cargos del Contrato</strong> por este monto.
              </p>
            </div>
          )}

          {/* Promo code input */}
          {value.mode === "promo" && (
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
                Código Promocional
              </label>
              <input
                type="text"
                value={value.promoCode}
                onChange={(e) => set({ promoCode: e.target.value.toUpperCase() })}
                placeholder="INVITACION2025"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors font-mono uppercase"
              />
              <p className="text-slate-400 text-xs mt-1.5">El código queda registrado para referencia interna.</p>
            </div>
          )}

          {/* Gift info */}
          {value.mode === "gift" && (
            <div className="flex items-start gap-2 bg-emerald-50 rounded-lg px-3 py-2.5 text-xs text-emerald-700">
              <i className="ri-gift-line mt-0.5 shrink-0"></i>
              <span>La invitación digital será activada sin costo para el cliente y se registrará como regalo.</span>
            </div>
          )}

          {/* Summary when active mode */}
          {value.mode !== "none" && (
            <div className="flex items-center gap-2 bg-[#C8A96E]/5 border border-[#C8A96E]/20 rounded-lg px-3 py-2.5">
              <i className="ri-checkbox-circle-fill text-[#C8A96E] text-sm shrink-0"></i>
              <p className="text-xs text-slate-600">
                {value.mode === "paid" && `Se activará la invitación y se agregará un cargo de $${value.amount} al contrato.`}
                {value.mode === "gift" && "Se activará la invitación digital como obsequio ($0). Se creará la invitación vacía lista para diseñar."}
                {value.mode === "promo" && `Se activará con código "${value.promoCode || "…"}". Sin cargo adicional.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}